import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import HomePage from './pages/HomePage.tsx';
import LoginPage from './pages/LoginPage.tsx';
import VerplegingWensenlijsten from './pages/VerplegingWensenlijsten.tsx';
import Verpleegkundigen from './pages/Verpleegkundigen.tsx';
import OverzichtVerpleegkundigen from './pages/OverzichtVerpleegkundigen.tsx';
import NieuweGebruiker from './pages/NieuweGebruiker.tsx';
import Rondes from './pages/Rondes.tsx';
import RondeDetail from './pages/RondeDetail.tsx';
import VerpleegkundigeDetail from './pages/VerpleegkundigeDetail.tsx';
import NieuweRonde from './pages/NieuweRonde.tsx';
import VerplegingOverzicht from './pages/VerplegingOverzicht.tsx';
import UurRooster from './pages/UurRooster.tsx';
import PersoonlijkOverzicht from './pages/PersoonlijkOverzicht.tsx';
import WachtwoordVergeten from './pages/WachtwoordVergeten.tsx';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ResetPassword from './pages/ResetPassword.tsx';


// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#4aa8b4', 
    },
    secondary: {
      main: '#FF5722', 
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none', 
        },
      },
    },
  },
});

// Root redirect component
const RootRedirect: React.FC = () => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return null; 
  }

  if (!isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return <Navigate to="/home" replace />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
  <CssBaseline />
    <AuthProvider>
      <Router>
          <Routes>
            {/* Root redirect */}
            <Route path="/" element={<RootRedirect />} />
            
            {/* Public homepage */}
            <Route path="/home" element={<HomePage />} />
            
            {/* Login page */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Wachtwoord vergeten page */}
            <Route path="/wachtwoord-vergeten" element={<WachtwoordVergeten />} />

            {/* Reset wachtwoord pagina via e-mail token */}
            <Route path="/reset-password/:token" element={<ResetPassword  />}/>
            
          
            
            {/* Verpleging Wensenlijsten - protected route */}
            <Route 
              path="/wensenlijsten" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <VerplegingWensenlijsten />
                </ProtectedRoute>
              } 
            />
            
            {/* Verpleegkundigen - protected route */}
            <Route 
              path="/verpleegkundigen" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <Verpleegkundigen />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/verpleegkundigen/:id/*"
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <VerpleegkundigeDetail />
                </ProtectedRoute>
              }
            />
            
            {/* Nieuwe Gebruiker - protected route */}
            <Route 
              path="/verpleegkundigen/nieuw" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <NieuweGebruiker />
                </ProtectedRoute>
              } 
            />
            
            {/* Overzicht Verpleegkundigen - protected route */}
            <Route 
              path="/overzicht-verpleegkundigen/:year/:month" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <OverzichtVerpleegkundigen />
                </ProtectedRoute>
              } 
            />
            
            {/* Rondes - protected route */}
            <Route 
              path="/rondes" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <Rondes />
                </ProtectedRoute>
              } 
            />
            
            {/* Nieuwe Ronde - protected route */}
            <Route 
              path="/rondes/nieuw" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <NieuweRonde />
                </ProtectedRoute>
              } 
            />
            
            {/* Ronde Detail - protected route */}
            <Route 
              path="/rondes/:id" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <RondeDetail />
                </ProtectedRoute>
              }
            />
         
            {/* Verpleging Overzicht - protected route */}
            <Route 
              path="/overzicht" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <VerplegingOverzicht />
                </ProtectedRoute>
              } 
            />
            
            {/* Uur Rooster - protected route */}
            <Route 
              path="/uurrooster" 
              element={
                <ProtectedRoute requiredRole={['nurse', 'admin']}>
                  <UurRooster />
                </ProtectedRoute>
              } 
            />
            
            {/* Persoonlijk Overzicht - ONLY for nursing staff */}
            <Route 
              path="/persoonlijk-overzicht" 
              element={
                <ProtectedRoute requiredRole="nurse">
                  <PersoonlijkOverzicht />
                </ProtectedRoute>
              } 
            />
          
            
            {/* Catch all - redirect to root */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
              </Router>
    </AuthProvider>
</ThemeProvider>

  );
};

export default App;
